/**
 * Reminders & expiry on a live envelope (DocuSign "Advanced options").
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, BellRing } from "lucide-react";
import { toast } from "sonner";
import { getEnvelopeNotification, putEnvelopeNotification } from "@/lib/api";

const Num = ({ id, label, value, onChange, min = 0, disabled }) => (
  <div>
    <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
    <Input id={id} type="number" min={min} value={value} disabled={disabled} className="h-8 mt-1"
      onChange={(e) => onChange(Math.max(min, parseInt(e.target.value || "0", 10) || 0))} data-testid={id} />
  </div>
);

export const EnvelopeNotificationDialog = ({ envelope, open, onClose }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !envelope?.envelope_id) return;
    setForm(null); setLoading(true);
    getEnvelopeNotification(envelope.envelope_id)
      .then((d) => setForm({ reminders: d.reminders, expirations: d.expirations, defaults: d.use_account_defaults }))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Could not load reminder settings"); onClose(); })
      .finally(() => setLoading(false));
  }, [open, envelope?.envelope_id, onClose]);

  const setRem = (k, v) => setForm((f) => {
    const reminders = { ...f.reminders, [k]: v };
    if (k === "enabled" && v) {
      if (!(reminders.frequency_days >= 1)) reminders.frequency_days = 2;
      if (!(reminders.delay_days >= 1)) reminders.delay_days = 1;
    }
    return { ...f, reminders };
  });
  const setExp = (k, v) => setForm((f) => {
    const expirations = { ...f.expirations, [k]: v };
    if (k === "enabled" && v && !(expirations.expire_after_days >= 1)) expirations.expire_after_days = 30;
    return { ...f, expirations };
  });

  const save = async () => {
    setSaving(true);
    try {
      await putEnvelopeNotification(envelope.envelope_id, { reminders: form.reminders, expirations: form.expirations });
      toast.success("Reminder & expiry settings saved on the envelope");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="envelope-notification-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BellRing className="w-4 h-4" /> Reminders & expiry</DialogTitle>
          <DialogDescription className="truncate">{envelope?.subject}</DialogDescription>
        </DialogHeader>
        {loading || !form ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
        ) : (
          <div className="space-y-5">
            {form.defaults && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">Currently using the DocuSign account defaults — saving here sets values just for this envelope.</p>}
            <div className="rounded-lg border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Automatic reminders</p>
                <Switch checked={form.reminders.enabled} onCheckedChange={(v) => setRem("enabled", v)} data-testid="reminders-enabled" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Num id="reminder-delay" label="First reminder after (days)" value={form.reminders.delay_days} onChange={(v) => setRem("delay_days", v)} disabled={!form.reminders.enabled} />
                <Num id="reminder-frequency" label="Then every (days)" value={form.reminders.frequency_days} min={1} onChange={(v) => setRem("frequency_days", v)} disabled={!form.reminders.enabled} />
              </div>
            </div>
            <div className="rounded-lg border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Envelope expiry</p>
                <Switch checked={form.expirations.enabled} onCheckedChange={(v) => setExp("enabled", v)} data-testid="expiry-enabled" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Num id="expire-after" label="Expires (days after sending)" value={form.expirations.expire_after_days} min={1} onChange={(v) => setExp("expire_after_days", v)} disabled={!form.expirations.enabled} />
                <Num id="expire-warn" label="Warn signers (days before)" value={form.expirations.warn_days} onChange={(v) => setExp("warn_days", v)} disabled={!form.expirations.enabled} />
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading || !form} data-testid="save-notification-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <BellRing className="w-4 h-4 mr-1" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
